import { createContext, useContext, useState, useEffect } from 'react';
import { usePageVisibility } from '@/hooks/usePageVisibility';

interface MonsterKills {
  clot: number
  gorefast: number
  crawler: number
  stalker: number
  bloat: number
  siren: number
  husk: number
  scrake: number
  fleshpound: number
  boss: number
  other: number
}

export interface PlayerData {
  name: string
  steamId: string
  ip: string
  perk: string
  perkLevel: number
  kills: number
  monsterKills?: MonsterKills
  cash: number
  health: number
  maxHealth: number
  deaths: number
  ping: number
}

export interface GameState {
  wave: { current: number; total: number; inProgress: boolean; traderTime: boolean }
  zeds: { alive: number; maxAtOnce: number; remainingThisWave?: number; totalThisWave?: number }
  difficulty: string
  difficultyNum: number
  map: string
  players: PlayerData[]
  spectators: { name: string; steamId: string; ip: string }[]
  playerCount: number
  spectatorCount: number
  mapElapsedSeconds: number
}

interface GameStateContextValue {
  data: GameState | null;
  online: boolean;
}

const GameStateContext = createContext<GameStateContextValue | null>(null);

export function useGameState() {
  const ctx = useContext(GameStateContext);
  if (!ctx) throw new Error('useGameState must be used within GameStateProvider');
  return ctx;
}

export function GameStateProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<GameState | null>(null);
  const [online, setOnline] = useState(false);
  const isVisible = usePageVisibility();

  useEffect(() => {
    if (!isVisible) return;

    let active = true;
    let timeoutId: number | null = null;
    let inFlight = false;
    let abortController: AbortController | null = null;
    let delayMs = 2000;
    let healthyCycles = 0;

    const jitter = (ms: number) => {
      const delta = Math.round((Math.random() - 0.5) * 1000);
      return Math.max(1000, ms + delta);
    };

    function schedule(nextDelay: number) {
      if (!active) return;
      if (timeoutId != null) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        void poll();
      }, jitter(nextDelay));
    }

    async function poll() {
      if (!active || inFlight) return;
      inFlight = true;
      abortController = new AbortController();
      try {
        const res = await fetch('/map-api/game-state', { signal: abortController.signal });
        if (!res.ok) {
          if (active) setOnline(false);
          healthyCycles = 0;
          delayMs = Math.min(30000, delayMs * 2);
          schedule(delayMs);
          return;
        }
        const json = await res.json();
        if (json.error) {
          if (active) setOnline(false);
          healthyCycles = 0;
          delayMs = Math.min(30000, delayMs * 2);
          schedule(delayMs);
          return;
        }
        if (active) {
          setData(json);
          setOnline(true);
        }
        healthyCycles += 1;
        if (healthyCycles >= 3) delayMs = 2000;
        schedule(delayMs);
      } catch (error) {
        if (active && !(error instanceof DOMException && error.name === 'AbortError')) {
          setOnline(false);
          healthyCycles = 0;
          delayMs = Math.min(30000, delayMs * 2);
          schedule(delayMs);
        }
      } finally {
        abortController = null;
        inFlight = false;
      }
    }

    void poll();
    return () => {
      active = false;
      if (timeoutId != null) window.clearTimeout(timeoutId);
      if (abortController) abortController.abort();
    };
  }, [isVisible]);

  return (
    <GameStateContext.Provider value={{ data, online }}>
      {children}
    </GameStateContext.Provider>
  );
}
